import { useIntl } from 'react-intl';
import type { ReactNode } from 'react';
import type { IntlShape } from 'react-intl';
import type { ChipProps, Theme } from '@mui/material';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { alpha, Chip, Stack, Typography, useTheme } from '@mui/material';
import PauseCircleOutlineIcon from '@mui/icons-material/PauseCircleOutline';
import { BOOK_BANK_STATUS_DISPLAY_ORDER, BOOK_BANK_STATUS_TYPE_ENUM, getBookBankStatusTypeTranslated } from 'enums';

type BankStatusChipProps = {
  status: BOOK_BANK_STATUS_TYPE_ENUM;
  count?: number;
  sx?: ChipProps['sx'];
};

type BankStatusSummaryProps = {
  counts: Record<BOOK_BANK_STATUS_TYPE_ENUM, number>;
  showEmptyStatuses?: boolean;
};

export const getBankStatusVisual = (status: BOOK_BANK_STATUS_TYPE_ENUM, theme: Theme) => {
  switch (status) {
    case BOOK_BANK_STATUS_TYPE_ENUM.COMPLETED:
      return {
        color: theme.palette.mode === 'light' ? theme.palette.success.dark : theme.palette.success.light,
        backgroundColor: alpha(theme.palette.success.main, theme.palette.mode === 'dark' ? 0.22 : 0.12),
        borderColor: alpha(theme.palette.success.main, 0.4),
        icon: <CheckCircleIcon fontSize="inherit" />,
      };
    case BOOK_BANK_STATUS_TYPE_ENUM.CONTINUES:
      return {
        color: theme.palette.mode === 'light' ? theme.palette.warning.dark : theme.palette.warning.light,
        backgroundColor: alpha(theme.palette.warning.main, theme.palette.mode === 'dark' ? 0.22 : 0.12),
        borderColor: alpha(theme.palette.warning.main, 0.4),
        icon: <AutorenewIcon fontSize="inherit" />,
      };
    case BOOK_BANK_STATUS_TYPE_ENUM.PASSIVE:
      return {
        color: theme.palette.mode === 'light' ? theme.palette.grey[700] : theme.palette.grey[300],
        backgroundColor: alpha(theme.palette.grey[500], theme.palette.mode === 'dark' ? 0.2 : 0.12),
        borderColor: alpha(theme.palette.grey[500], 0.4),
        icon: <PauseCircleOutlineIcon fontSize="inherit" />,
      };
    case BOOK_BANK_STATUS_TYPE_ENUM.NONE:
    default:
      return {
        color: theme.palette.text.secondary,
        backgroundColor: alpha(theme.palette.text.secondary, 0.1),
        borderColor: alpha(theme.palette.text.secondary, 0.3),
        icon: <HelpOutlineIcon fontSize="inherit" />,
      };
  }
};

export const getBankStatusLabel = (status: BOOK_BANK_STATUS_TYPE_ENUM, intl: IntlShape): string => {
  switch (status) {
    case BOOK_BANK_STATUS_TYPE_ENUM.COMPLETED:
      return intl.formatMessage({ id: 'enum.bookBankStatusType.completed', defaultMessage: 'Tamamlandı' });
    case BOOK_BANK_STATUS_TYPE_ENUM.CONTINUES:
      return intl.formatMessage({ id: 'enum.bookBankStatusType.continues', defaultMessage: 'Devam Ediyor' });
    case BOOK_BANK_STATUS_TYPE_ENUM.PASSIVE:
      return intl.formatMessage({ id: 'enum.bookBankStatusType.passive', defaultMessage: 'Pasif' });
    case BOOK_BANK_STATUS_TYPE_ENUM.NONE:
    default:
      return intl.formatMessage({ id: 'enum.bookBankStatusType.none', defaultMessage: 'Belirsiz' });
  }
};

const BankStatusChip = ({ status, count, sx }: BankStatusChipProps) => {
  const theme = useTheme();
  const visual = getBankStatusVisual(status, theme);
  const translatedStatus = getBookBankStatusTypeTranslated(status);
  const label: ReactNode =
    count === undefined ? (
      translatedStatus
    ) : (
      <>
        {translatedStatus} · {count}
      </>
    );

  return (
    <Chip
      icon={visual.icon}
      label={label}
      size="small"
      sx={[
        {
          height: 24,
          color: visual.color,
          bgcolor: visual.backgroundColor,
          border: `1px solid ${visual.borderColor}`,
          fontSize: '0.72rem',
          fontWeight: 600,
          '& .MuiChip-icon': { color: 'inherit' },
        },
        ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
      ]}
    />
  );
};

export const BankStatusSummary = ({ counts, showEmptyStatuses = false }: BankStatusSummaryProps) => {
  const intl = useIntl();
  const statuses = BOOK_BANK_STATUS_DISPLAY_ORDER.filter((status) => showEmptyStatuses || counts[status] > 0);

  if (statuses.length === 0) {
    return (
      <Typography
        component="span"
        variant="body2"
        color="text.disabled"
        aria-label={intl.formatMessage({ id: 'report.noBankStatus', defaultMessage: 'Banka durumu yok' })}>
        —
      </Typography>
    );
  }

  return (
    <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap" alignItems="center">
      {statuses.map((status) => (
        <BankStatusChip key={status} status={status} count={counts[status]} />
      ))}
    </Stack>
  );
};

export default BankStatusChip;
